import * as React from 'react';
import {
  AddTile,
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
  Button,
  ChargeComposer,
  DetailField,
  Form,
  FormField,
  LineItemList,
  Alert,
  Loading,
  OptionSheet,
  Stack,
  applyServerErrors,
  toast,
  useZodForm,
} from '@relay-towing/ui';
import type { CallPaymentDto } from '@relay-towing/api';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCallImpound } from '../../hooks/useCallImpound';
import { useCreateQuickInvoice } from '../../hooks/useCreateQuickInvoice';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import { useGetCustomerRates } from '../../hooks/useGetCustomerRates';
import { useMember } from '../../hooks/useMember';
import { usePaymentLink } from '../../hooks/usePaymentLink';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { companyIdFromProfileOrStorage } from '../../hooks/userCompanies.shared';
import type { Impound, Rate } from '../../lib/calculations';
import {
  createInvoiceSchema,
  type CreateInvoiceValues,
} from '../../lib/createInvoiceSchema';
import {
  lineItemsFromRates,
  pricingCallOf,
  quickCharges,
  withCharge,
} from '../../lib/invoiceLines';
import type { CallRecord } from './callRecord';

/**
 * Raising a call's invoice on a phone, opened from the call's Invoice
 * section with "Create invoices" while the call has none. There is no Figma
 * frame for it: it follows the old payment screen's create step, laid out
 * as CreateCallSheet is, one sheet with Cancel and Create at its foot. The
 * charges start priced from the account's rates (lineItemsFromRates, as the
 * old screen and the dashboard price them), or, with no rate to price
 * from, the call's charges as one line, as the server would bill it. Each
 * quantity steps, and a charge is added typed in or from the account's
 * other rates.
 *
 * The invoice is made out to the call's contact: the server takes the
 * customer from the call, so the name is shown rather than asked. Card is
 * offered only while the company has a payment provider connected. Once it
 * is made, a member who may take payment goes on to the call's payment,
 * with a card invoice's payment link made for them.
 */
export function CreateInvoiceSheet({
  call,
  open,
  onOpenChange,
}: {
  call: CallRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: profile } = useDriverProfile();
  const companyId = companyIdFromProfileOrStorage(profile) || undefined;
  const payment = usePaymentStatus(open ? call.id : undefined, companyId);
  const rates = useGetCustomerRates(
    { customerId: call.account ? String(call.account) : null, companyId },
    { enabled: open }
  );
  const impound = useCallImpound(call.id, companyId, {
    enabled: open && !!call.impound,
  });
  const loading = !payment.data || rates.isLoading || impound.isLoading;

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      {payment.isError ? (
        <BottomSheetContent title="Create invoice" size="full">
          <Alert tone="destructive">
            The call’s payment could not be loaded. Close this and try again.
          </Alert>
        </BottomSheetContent>
      ) : loading ? (
        <BottomSheetContent title="Create invoice" size="full">
          <Loading label="Pricing the call" />
        </BottomSheetContent>
      ) : (
        <CreateInvoiceForm
          call={call}
          status={payment.data!}
          rates={rates.data ?? []}
          impound={impound.data ?? null}
          companyId={companyId}
          onDone={() => onOpenChange(false)}
        />
      )}
    </BottomSheet>
  );
}

function CreateInvoiceForm({
  call,
  status,
  rates,
  impound,
  companyId,
  onDone,
}: {
  call: CallRecord;
  status: CallPaymentDto;
  rates: Rate[];
  impound: Impound | null;
  companyId: string | undefined;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const member = useMember();
  const formId = React.useId();
  const [adding, setAdding] = React.useState(false);
  const create = useCreateQuickInvoice(companyId);
  const link = usePaymentLink(call.id, companyId);

  const priced = lineItemsFromRates(rates, pricingCallOf(call), impound);
  const form = useZodForm(createInvoiceSchema, {
    defaultValues: {
      payment: status.paymentConnected ? 'CARD' : 'CASH',
      lines:
        priced.length || status.suggestedTotal <= 0
          ? priced
          : [
              {
                description: 'Towing Service',
                quantity: 1,
                unitPrice: status.suggestedTotal,
              },
            ],
    },
  });
  const suggestions = quickCharges(rates);

  const submit = async (values: CreateInvoiceValues) => {
    let made;
    try {
      made = await create.mutateAsync({
        dispatchId: call.id,
        items: values.lines,
        isCashPayment: values.payment === 'CASH',
      });
    } catch (error) {
      applyServerErrors(form, error, 'The invoice could not be created.');
      return;
    }
    const takesPayment = member.can('invoices.take_payment');
    // A card invoice's link is made with it, as the old screen made it, so
    // the payment opens on its code. If that fails the payment screen
    // offers to make it again; the invoice stands either way.
    if (takesPayment && values.payment === 'CARD' && made?.invoice?.id)
      await link.make(made.invoice.id).catch(() => undefined);
    onDone();
    toast.success('Invoice created');
    // To the invoice either way, as the old screen stayed on it: without
    // the key it says an owner or admin collects the payment.
    navigate(`/calls/${call.id}/payment`);
  };

  return (
    <BottomSheetContent
      title="Create invoice"
      size="full"
      footer={
        <>
          <BottomSheetClose asChild>
            <Button
              variant="secondary"
              size="xl"
              pill
              disabled={create.isPending}
            >
              Cancel
            </Button>
          </BottomSheetClose>
          <Button
            type="submit"
            form={formId}
            size="xl"
            pill
            loading={create.isPending || link.making}
          >
            Create
          </Button>
        </>
      }
    >
      <Form form={form} id={formId} gap={6} onSubmit={submit}>
        <DetailField label="Customer" value={status.contactName} />
        <FormField
          form={form}
          name="payment"
          label="Payment"
          description={
            status.paymentConnected
              ? undefined
              : 'Card payments need a payment provider connected. Ask your owner to connect one.'
          }
        >
          {(field) => (
            <OptionSheet
              title="Payment"
              options={[
                ...(status.paymentConnected
                  ? [{ value: 'CARD', label: 'Card, with a payment link' }]
                  : []),
                { value: 'CASH', label: 'Cash' },
              ]}
              value={field.value}
              onValueChange={field.onChange}
            />
          )}
        </FormField>
        <FormField form={form} name="lines" label="Charges">
          {(field) => (
            <Stack gap={3}>
              <LineItemList
                items={field.value.map((line) => ({
                  description: line.description,
                  qty: line.quantity,
                  rate: line.unitPrice,
                }))}
                onQuantityChange={(index, quantity) =>
                  field.onChange(
                    field.value.map((line, i) =>
                      i === index ? { ...line, quantity } : line
                    )
                  )
                }
                onRemove={(index) =>
                  field.onChange(field.value.filter((_, i) => i !== index))
                }
              />
              <AddTile
                width="block"
                appearance="solid"
                icon={Plus}
                onClick={() => setAdding(true)}
              >
                Add a charge
              </AddTile>
              <ChargeComposer
                open={adding}
                onOpenChange={setAdding}
                suggestions={suggestions.map((charge) => ({
                  id: charge.id,
                  description: charge.description,
                  amount: charge.unitPrice,
                }))}
                onSubmit={({ description, amount }) => {
                  // A rate tapped twice is one line of two; a charge typed
                  // in is its own line, as it may be priced differently.
                  const rate = suggestions.find(
                    (charge) =>
                      charge.description === description &&
                      charge.unitPrice === amount
                  );
                  field.onChange(
                    rate
                      ? withCharge(field.value, rate)
                      : [
                          ...field.value,
                          { description, quantity: 1, unitPrice: amount },
                        ]
                  );
                  setAdding(false);
                }}
              />
            </Stack>
          )}
        </FormField>
      </Form>
    </BottomSheetContent>
  );
}
